import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { ParticipantLayout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { 
  Video, FileText, Image, Music, Download, Search, Filter, ExternalLink,
  Clock, HardDrive, Bookmark, BookmarkCheck
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEDIA_TYPE_ICONS = {
  video: Video,
  document: FileText,
  image: Image,
  audio: Music
};

const DIFFICULTY_LABELS = {
  anfaenger: { label: "Anfänger", color: "bg-green-500/10 text-green-500" },
  fortgeschritten: { label: "Fortgeschritten", color: "bg-yellow-500/10 text-yellow-500" },
  experte: { label: "Experte", color: "bg-red-500/10 text-red-500" }
};

const Medienbibliothek = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedItems, setSavedItems] = useState([]);
  
  // Filters
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedDifficulty, setSelectedDifficulty] = useState("all");

  useEffect(() => {
    loadData();
  }, [selectedCategory, selectedDifficulty]);

  const loadData = async () => {
    try {
      const params = {};
      if (selectedCategory !== "all") params.category = selectedCategory;
      if (selectedDifficulty !== "all") params.difficulty = selectedDifficulty;
      if (search) params.search = search;
      
      const [itemsRes, categoriesRes, savedRes] = await Promise.all([
        axios.get(`${API}/media`, { params }),
        axios.get(`${API}/media/categories`),
        axios.get(`${API}/lernraum/saved`)
      ]);
      
      setItems(itemsRes.data);
      setCategories(categoriesRes.data);
      setSavedItems(savedRes.data.filter(s => s.content_type === "media").map(s => s.content_id));
    } catch (error) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadData();
  };

  const handleSave = async (mediaId) => {
    try {
      if (savedItems.includes(mediaId)) {
        // Find and remove
        const savedRes = await axios.get(`${API}/lernraum/saved`);
        const savedItem = savedRes.data.find(s => s.content_type === "media" && s.content_id === mediaId);
        if (savedItem) {
          await axios.delete(`${API}/lernraum/saved/${savedItem.id}`);
          setSavedItems(prev => prev.filter(id => id !== mediaId));
          toast.success("Aus Lernraum entfernt");
        }
      } else {
        await axios.post(`${API}/lernraum/save`, null, {
          params: { content_type: "media", content_id: mediaId }
        });
        setSavedItems(prev => [...prev, mediaId]);
        toast.success("Zu Lernraum hinzugefügt");
      }
    } catch (error) {
      toast.error("Fehler");
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return null;
    if (minutes < 60) return `${minutes} Min.`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatFileSize = (mb) => {
    if (!mb) return null;
    if (mb < 1) return `${Math.round(mb * 1000)} KB`;
    return `${mb.toFixed(1)} MB`;
  };

  const MediaIcon = ({ type }) => {
    const Icon = MEDIA_TYPE_ICONS[type] || FileText;
    return <Icon className="h-5 w-5" />;
  };

  return (
    <ParticipantLayout title="Medienbibliothek">
      <div className="space-y-6" data-testid="media-library-page">
        {/* Header */}
        <div>
          <p className="text-muted-foreground">
            Lernvideos, Dokumente, Prüfungsaufgaben und mehr.
          </p>
        </div>

        {/* Filters */}
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 flex gap-2">
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Suchen..."
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                />
                <Button variant="outline" onClick={handleSearch}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Category Filter */}
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Kategorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Kategorien</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Difficulty Filter */}
              <Select value={selectedDifficulty} onValueChange={setSelectedDifficulty}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Schwierigkeit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Stufen</SelectItem>
                  <SelectItem value="anfaenger">Anfänger</SelectItem>
                  <SelectItem value="fortgeschritten">Fortgeschritten</SelectItem>
                  <SelectItem value="experte">Experte</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-12 text-center text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Keine Medien gefunden</p>
              <p className="text-sm mt-1">Ändern Sie Ihre Filter oder Suche.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map(item => {
              const diffConfig = DIFFICULTY_LABELS[item.difficulty];
              const isSaved = savedItems.includes(item.id);
              
              return (
                <Card key={item.id} className="bg-card border-border hover:border-primary/50 transition-colors group">
                  {/* Thumbnail */}
                  <div className="relative h-40 bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                    <MediaIcon type={item.media_type} />
                    <div className="absolute top-2 right-2 flex gap-1">
                      <Badge variant="secondary">{item.category}</Badge>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => handleSave(item.id)}
                    >
                      {isSaved ? (
                        <BookmarkCheck className="h-5 w-5 text-primary" />
                      ) : (
                        <Bookmark className="h-5 w-5" />
                      )}
                    </Button>
                  </div>
                  
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base line-clamp-1">{item.title}</CardTitle>
                    {item.description && (
                      <CardDescription className="line-clamp-2">{item.description}</CardDescription>
                    )}
                  </CardHeader>
                  
                  <CardContent>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {diffConfig && (
                        <Badge className={`${diffConfig.color} border`}>
                          {diffConfig.label}
                        </Badge>
                      )}
                      {item.duration_minutes && (
                        <Badge variant="outline" className="text-xs">
                          <Clock className="h-3 w-3 mr-1" />
                          {formatDuration(item.duration_minutes)}
                        </Badge>
                      )}
                      {item.file_size_mb && (
                        <Badge variant="outline" className="text-xs">
                          <HardDrive className="h-3 w-3 mr-1" />
                          {formatFileSize(item.file_size_mb)}
                        </Badge>
                      )}
                    </div>
                    
                    {item.dozent_name && (
                      <p className="text-xs text-muted-foreground mb-3">
                        Dozent: {item.dozent_name}
                      </p>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        {item.download_count} Downloads
                      </span>
                      <Button size="sm" asChild>
                        <a href={item.url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4 mr-1" />
                          Öffnen
                        </a>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </ParticipantLayout>
  );
};

export default Medienbibliothek;
